import { useEffect } from 'react'
import { Outlet, useLoaderData, ScrollRestoration, useLocation } from '@modern-js/runtime/router'

import { useAnalyticsSetup } from '@fe-toolkit/analytics/react'
import { setupEventingBridge } from '@fe-toolkit/analytics'
import { tamaguiConfig } from '@fe-toolkit/ui'
import { FederatedComponentBoundary } from '@fe-toolkit/web-components/ssr'

import CommonHeader from 'fe_common/components/CommonHeader'

import CommonFooter from 'fe_common/components/CommonFooter'

import Providers from '@/components/common/Providers'
import FallbackFooter from '@/components/fallbacks/FallbackFooter'
import FallbackHeader from '@/components/fallbacks/FallbackHeader'

import { RuntimeConfigContext, serializeConfigForScript, mfeName, basePath } from '@/config/runtimeConfig'

import { buildFAQSchema } from 'utils/getFaqSchema'

import { buildRoamingSchema } from '@/utils/buildRoamingSchema'

import type { RuntimeConfig } from '@/config/runtimeConfig'
import type { SafeMemberDetails } from '@/api/member/getMemberDetailsForRequest'

export default function Layout() {
  const { memberDetails, runtimeConfig } = useLoaderData() as {
    memberDetails: SafeMemberDetails | null
    runtimeConfig: RuntimeConfig
  }

  const { pathname } = useLocation()

  // Initialise Snowplow tracker and wire up OneTrust consent on the client.
  // Guard with ENABLE_SNOWPLOW so the tracker can be toggled per environment
  // without a rebuild.
  //
  // anonymousTracking: tracker starts without persistent identifiers so the
  // automatic on-init page view carries no cookies or network_userid. On
  // OnConsentChanged, setup.ts automatically calls disableAnonymousTracking,
  // fires a sessionisation trackPageView, then reports consent.
  useAnalyticsSetup({
    enabled: runtimeConfig.ENABLE_SNOWPLOW === 'true',
    collectorUrl: runtimeConfig.SNOWPLOW_COLLECTOR_URL,
    appId: mfeName || 'web-mfe',
    anonymousTracking: { withServerAnonymisation: true },
    cookieSecure: true,
    basePath,
    pathname,
  })

  // The bridge sends customEvent calls directly to the Snowplow tracker. This
  // preserves anonymous pre-consent tracking when GTM Consent Mode gates its
  // analytics_storage data layer.
  useEffect(() => {
    if (runtimeConfig.ENABLE_SNOWPLOW === 'true') {
      setupEventingBridge()
    }
  }, [runtimeConfig.ENABLE_SNOWPLOW])

  // Serialize runtime config as an inline script so it's available to non-React
  // code (API client, MSW init in modern.runtime.ts, etc.) BEFORE the main
  // bundle's module-init code runs. The useEffect approach was too late —
  // modern.runtime.ts executes at import time, before React hydration.
  //
  // serializeConfigForScript escapes `<`, `\u2028`, and `\u2029` to prevent
  // script-tag breakout and XSS via crafted config values.
  //
  // CSP note: This inline <script> requires either 'unsafe-inline' or a
  // nonce (e.g. `script-src 'nonce-<random>'`) in the Content-Security-Policy
  // header. If a strict CSP is introduced, generate a per-request nonce in
  // the SSR response and attach it via `<script nonce={nonce}>`. Modern.js
  // custom server middleware can inject the nonce into both the CSP header
  // and the React render context.
  const runtimeConfigScript = `window.__RUNTIME_CONFIG__=${serializeConfigForScript(runtimeConfig)};`

  return (
    <>
      <RuntimeConfigContext.Provider value={runtimeConfig}>
        <script dangerouslySetInnerHTML={{ __html: runtimeConfigScript }} />
        <style
          dangerouslySetInnerHTML={{ __html: tamaguiConfig.getCSS() }}
          suppressHydrationWarning
          id="tamagui-base"
        />
        <script
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFAQSchema(), null, 2) }}
          type="application/ld+json"
        />
        <script
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildRoamingSchema(), null, 2) }}
          type="application/ld+json"
        />
        <Providers memberId={memberDetails?.id} runtimeConfig={runtimeConfig}>
          <ScrollRestoration />
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <FederatedComponentBoundary
              fallback={<FallbackHeader username={memberDetails?.memberName} />}
              logLabel="fe_common Header"
            >
              <CommonHeader username={memberDetails?.memberName} />
            </FederatedComponentBoundary>
            <main aria-labelledby="main-title" id="main-content" style={{ flex: 1 }}>
              <Outlet />
            </main>
            <FederatedComponentBoundary fallback={<FallbackFooter />} logLabel="fe_common Footer">
              <CommonFooter />
            </FederatedComponentBoundary>
          </div>
        </Providers>
      </RuntimeConfigContext.Provider>
    </>
  )
}
